import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { ReportAccessGuard } from '../../components/ReportAccessGuard';
import { ReportDownloadProgressView } from '../../components/ReportDownloadProgress';
import { ScreenHeader } from '../../components/ScreenHeader';
import { REPORT_ACCESS_CODES } from '../../config/reportCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { routes } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { getEmployeeMonthlyAttendanceReport } from '../../services/attendanceService';
import type {
  EmployeeMonthlyAttendanceDay,
  EmployeeMonthlyAttendanceLegendEntry,
  EmployeeMonthlyAttendanceRow,
} from '../../types/employeeMonthlyAttendanceReport';
import type { ReportFileFormat } from '../../types/report';
import { attendanceErrorMessage } from '../../utils/attendanceErrors';
import {
  EMPLOYEE_MONTHLY_ATTENDANCE_REPORT_CODE,
  MONTHLY_TOTAL_COLUMNS,
  MONTH_SHORT_NAMES,
  currentReportMonth,
  formatMonthlyTotal,
  formatReportMonthLabel,
  monthlyDayCell,
  monthlyReportColumns,
  shiftReportMonth,
} from '../../utils/employeeMonthlyAttendanceReport';
import { hasPermission } from '../../utils/permissions';
import { reportErrorMessage } from '../../utils/reportErrors';
import { formFactorForWidth } from '../../utils/responsiveLayout';
import { statusColor } from './EmployeeOverallWeeklyReportScreen';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'EmployeeMonthlyAttendanceReport'
>;

const PAGE_SIZE = 20;
/** Header and data row heights; both table parts use them so rows line up. */
export const MONTHLY_HEADER_HEIGHT = 54;
export const MONTHLY_ROW_HEIGHT = 40;

/**
 * Column widths per form factor. The backend PDF (MONTHLY_LAYOUT in
 * employee-monthly-attendance-report.service.js) uses the desktop widths.
 */
export function monthlyColumnWidths(width: number) {
  const factor = formFactorForWidth(width);
  if (factor === 'mobile') return { employee: 120, day: 40, total: 76 };
  if (factor === 'tablet') return { employee: 168, day: 42, total: 84 };
  return { employee: 220, day: 44, total: 88 };
}

/** Day cell colour: the weekly report's colour for the day's full status. */
export function monthlyDayColor(
  day: EmployeeMonthlyAttendanceDay,
  palette: ColorPalette,
): string {
  if (day.code === '-') return palette.textSecondary;
  return statusColor(day.statusLabel || day.code, palette);
}

export function legendColor(
  entry: EmployeeMonthlyAttendanceLegendEntry,
  palette: ColorPalette,
): string {
  if (entry.kind === 'leave') return palette.info;
  return statusColor(entry.statusLabel ?? entry.label, palette);
}

function EmployeeMonthlyAttendanceReportBody({ navigation }: Props) {
  const palette = useAppColors();
  const { permissions } = useAuth();
  const downloads = useReportDownloads();
  const { width } = useWindowDimensions();
  const columns = monthlyColumnWidths(width);
  const [period, setPeriod] = useState(() => currentReportMonth());
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [items, setItems] = useState<EmployeeMonthlyAttendanceRow[]>([]);
  const [legend, setLegend] = useState<EmployeeMonthlyAttendanceLegendEntry[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);

  const canGoBack =
    typeof navigation.canGoBack === 'function' && navigation.canGoBack();
  const canExport = hasPermission(permissions, PERMISSIONS.REPORT_EXPORT);
  const dayColumns = useMemo(
    () => monthlyReportColumns(period.year, period.month),
    [period.month, period.year],
  );

  const activeProgress = useMemo(
    () =>
      downloads.activeDownloads.find(
        item =>
          item.downloadId.startsWith(`${EMPLOYEE_MONTHLY_ATTENDANCE_REPORT_CODE}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ) ?? null,
    [downloads.activeDownloads],
  );

  const fetchReport = useCallback(
    async (nextPage: number, isRefresh = false) => {
      if (!isRefresh) setLoading(true);
      setError(null);
      try {
        const result = await getEmployeeMonthlyAttendanceReport({
          year: period.year,
          month: period.month,
          search: appliedSearch,
          page: nextPage,
          pageSize: PAGE_SIZE,
        });
        setItems(result.items);
        setLegend(result.legend);
        setPage(result.pagination.page);
        setTotalPages(Math.max(result.pagination.totalPages, 1));
        setTotalRecords(result.pagination.totalRecords);
      } catch (caught) {
        setError(
          attendanceErrorMessage(
            caught,
            'Unable to load the employee monthly attendance report.',
          ),
        );
        setItems([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [appliedSearch, period.month, period.year],
  );

  useEffect(() => {
    void fetchReport(1, false);
  }, [fetchReport]);

  const handleDownload = async (format: Extract<ReportFileFormat, 'XLSX' | 'PDF'>) => {
    setDownloadError(null);
    setDownloadNotice(null);
    if (!canExport) {
      setDownloadError('You do not have permission to export reports.');
      return;
    }
    if (totalRecords === 0 && items.length === 0) {
      setDownloadError('No report data is available to download for this month.');
      return;
    }
    if (downloads.isDownloadInFlight(EMPLOYEE_MONTHLY_ATTENDANCE_REPORT_CODE, format)) {
      setDownloadError('A download is already in progress.');
      return;
    }
    try {
      const result = await downloads.startDownload({
        reportCode: EMPLOYEE_MONTHLY_ATTENDANCE_REPORT_CODE,
        reportName: 'Employee Monthly Attendance Report',
        format,
        filters: {},
        employeeMonthlyAttendanceQuery: {
          year: period.year,
          month: period.month,
          search: appliedSearch || undefined,
        },
      });
      if (Platform.OS === 'web') {
        setDownloadNotice(`Downloaded ${result.fileName}`);
        return;
      }
      navigation.navigate(routes.reportDownloadResult, {
        downloadId: result.downloadId,
      });
    } catch (err) {
      setDownloadError(reportErrorMessage(err, 'The report could not be saved.'));
    }
  };

  const rangeStart = totalRecords === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalRecords);
  const canDownload = !loading && !activeProgress;
  const monthLabel = formatReportMonthLabel(period.year, period.month);

  return (
      <AppScreen contentStyle={styles.screen} webLayout="wide">
        <ScreenHeader
          title="Employee Monthly Attendance Report"
          onBack={canGoBack ? () => navigation.goBack() : undefined}
        />
        <View style={styles.body}>
          <View
            style={[
              styles.monthBar,
              { backgroundColor: palette.surface, borderColor: palette.border },
            ]}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Previous month"
              onPress={() => setPeriod(current => shiftReportMonth(current, -1))}
              hitSlop={8}
            >
              <Text style={[styles.chevron, { color: palette.primary }]}>‹</Text>
            </Pressable>
            <Text style={[styles.monthLabel, { color: palette.textPrimary }]}>
              {monthLabel}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Next month"
              onPress={() => setPeriod(current => shiftReportMonth(current, 1))}
              hitSlop={8}
            >
              <Text style={[styles.chevron, { color: palette.primary }]}>›</Text>
            </Pressable>
          </View>
          <View style={styles.selectorRow}>
            <View style={styles.yearStepper}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Previous year"
                onPress={() => setPeriod(current => shiftReportMonth(current, -12))}
                hitSlop={8}
              >
                <Text style={[styles.yearChevron, { color: palette.primary }]}>‹</Text>
              </Pressable>
              <Text
                accessibilityLabel={`Year ${period.year}`}
                style={[styles.yearLabel, { color: palette.textPrimary }]}
              >
                {period.year}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Next year"
                onPress={() => setPeriod(current => shiftReportMonth(current, 12))}
                hitSlop={8}
              >
                <Text style={[styles.yearChevron, { color: palette.primary }]}>›</Text>
              </Pressable>
            </View>
            <View style={styles.monthChips}>
              {MONTH_SHORT_NAMES.map((name, index) => {
                const selected = period.month === index + 1;
                return (
                  <Pressable
                    key={name}
                    accessibilityRole="button"
                    accessibilityLabel={`${name} ${period.year}`}
                    accessibilityState={{ selected }}
                    onPress={() => setPeriod(current => ({ ...current, month: index + 1 }))}
                    style={[
                      styles.monthChip,
                      {
                        borderColor: selected ? palette.primary : palette.border,
                        backgroundColor: selected ? palette.primary : palette.surface,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.monthChipText,
                        { color: selected ? palette.white : palette.textPrimary },
                      ]}
                    >
                      {name}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="This month"
            onPress={() => setPeriod(currentReportMonth())}
          >
            <Text style={[styles.thisMonth, { color: palette.primary }]}>This month</Text>
          </Pressable>
          <TextInput
            accessibilityLabel="Search Employee"
            placeholder="Search Employee"
            placeholderTextColor={palette.textMuted}
            value={search}
            onChangeText={setSearch}
            onSubmitEditing={() => setAppliedSearch(search.trim())}
            returnKeyType="search"
            style={[
              styles.search,
              {
                backgroundColor: palette.surface,
                borderColor: palette.border,
                color: palette.textPrimary,
              },
            ]}
          />

          {canExport ? (
            <View style={styles.downloadRow}>
              <AppButton
                title="Download Excel"
                variant="secondary"
                accessibilityLabel="Download Report Excel"
                onPress={() => {
                  void handleDownload('XLSX');
                }}
                disabled={!canDownload}
                loading={downloads.isDownloadInFlight(
                  EMPLOYEE_MONTHLY_ATTENDANCE_REPORT_CODE,
                  'XLSX',
                )}
                style={styles.downloadButton}
              />
              <AppButton
                title="Download PDF"
                variant="secondary"
                accessibilityLabel="Download Report PDF"
                onPress={() => {
                  void handleDownload('PDF');
                }}
                disabled={!canDownload}
                loading={downloads.isDownloadInFlight(
                  EMPLOYEE_MONTHLY_ATTENDANCE_REPORT_CODE,
                  'PDF',
                )}
                style={styles.downloadButton}
              />
            </View>
          ) : null}
          {activeProgress ? (
            <ReportDownloadProgressView
              progress={activeProgress}
              canCancel
              onCancel={() => downloads.cancelDownload(activeProgress.downloadId)}
            />
          ) : null}
          {downloadError ? (
            <Text style={[styles.message, { color: palette.error }]}>{downloadError}</Text>
          ) : null}
          {downloadNotice ? (
            <Text style={[styles.message, { color: palette.success }]}>
              {downloadNotice}
            </Text>
          ) : null}

          {legend.length > 0 ? (
            <View accessibilityLabel="Attendance legend" style={styles.legend}>
              {legend.map(entry => (
                <View key={`${entry.kind}:${entry.code}`} style={styles.legendItem}>
                  <Text style={[styles.legendCode, { color: legendColor(entry, palette) }]}>
                    {entry.code}
                  </Text>
                  <Text style={[styles.legendLabel, { color: palette.textSecondary }]}>
                    {entry.label}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          {loading && items.length === 0 ? (
            <AppLoader message="Loading employee monthly attendance report" />
          ) : error && items.length === 0 ? (
            <ErrorState message={error} onRetry={() => fetchReport(page, false)} />
          ) : items.length === 0 ? (
            <EmptyState
              title="No data available"
              message="No employees matched this month and search."
            />
          ) : (
            <ScrollView
              style={styles.tableScroll}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={() => {
                    setRefreshing(true);
                    void fetchReport(page, true);
                  }}
                  tintColor={palette.primary}
                />
              }
            >
              <View
                style={[
                  styles.table,
                  { backgroundColor: palette.surface, borderColor: palette.border },
                ]}
              >
                {/* Employee names stay in place while the dates scroll sideways. */}
                <View
                  style={[
                    styles.frozenColumn,
                    { borderRightColor: palette.border },
                  ]}
                >
                  <View
                    style={[
                      styles.headerRow,
                      styles.frozenCell,
                      {
                        borderBottomColor: palette.border,
                        backgroundColor: palette.background,
                      },
                    ]}
                  >
                    <Text
                      accessibilityRole="header"
                      style={[
                        styles.headerCell,
                        { width: columns.employee, color: palette.textMuted },
                      ]}
                    >
                      Employee Name
                    </Text>
                  </View>
                  {items.map((item, index) => (
                    <View
                      key={String(item.employeeId)}
                      style={[
                        styles.dataRow,
                        styles.frozenCell,
                        {
                          borderBottomColor: palette.border,
                          borderBottomWidth:
                            index === items.length - 1 ? 0 : StyleSheet.hairlineWidth,
                        },
                      ]}
                    >
                      <Text
                        numberOfLines={1}
                        accessibilityLabel={
                          item.employeeNumber
                            ? `${item.employeeName}, ${item.employeeNumber}`
                            : item.employeeName
                        }
                        style={[
                          styles.employeeName,
                          { width: columns.employee, color: palette.textPrimary },
                        ]}
                      >
                        {item.employeeName}
                      </Text>
                    </View>
                  ))}
                </View>
                <ScrollView
                  horizontal
                  style={styles.datesScroll}
                  accessibilityLabel="Employee monthly attendance table"
                >
                  <View>
                    <View
                      style={[
                        styles.headerRow,
                        styles.scrollRow,
                        {
                          borderBottomColor: palette.border,
                          backgroundColor: palette.background,
                        },
                      ]}
                    >
                      {dayColumns.map(column => (
                        <View
                          key={column.date}
                          style={[styles.dayHeader, { width: columns.day }]}
                        >
                          <Text
                            accessibilityRole="header"
                            accessibilityLabel={`${column.dayNumber} ${column.weekday}`}
                            style={[styles.headerCell, styles.center, { color: palette.textMuted }]}
                          >
                            {column.dayNumber}
                          </Text>
                          <Text
                            style={[styles.dateCell, styles.center, { color: palette.textSecondary }]}
                          >
                            {column.weekday}
                          </Text>
                        </View>
                      ))}
                      {MONTHLY_TOTAL_COLUMNS.map(column => (
                        <Text
                          key={column.key}
                          accessibilityRole="header"
                          style={[
                            styles.headerCell,
                            { width: columns.total, color: palette.textMuted },
                          ]}
                        >
                          {column.title}
                        </Text>
                      ))}
                    </View>
                    {items.map((item, index) => (
                      <View
                        key={String(item.employeeId)}
                        style={[
                          styles.dataRow,
                          styles.scrollRow,
                          {
                            borderBottomColor: palette.border,
                            borderBottomWidth:
                              index === items.length - 1 ? 0 : StyleSheet.hairlineWidth,
                          },
                        ]}
                      >
                        {dayColumns.map(column => {
                          const day = monthlyDayCell(item, column.date);
                          const detail = [day.statusLabel, ...day.notes].join(', ');
                          return (
                            <Text
                              key={column.date}
                              accessibilityLabel={`${item.employeeName}, ${column.weekday} ${column.dayNumber}, ${detail}`}
                              numberOfLines={1}
                              adjustsFontSizeToFit
                              style={[
                                styles.statusCell,
                                styles.center,
                                {
                                  width: columns.day,
                                  color: monthlyDayColor(day, palette),
                                },
                              ]}
                            >
                              {day.code}
                            </Text>
                          );
                        })}
                        {MONTHLY_TOTAL_COLUMNS.map(column => {
                          const value = formatMonthlyTotal(item.totals[column.key]);
                          return (
                            <Text
                              key={column.key}
                              accessibilityLabel={`${item.employeeName} ${column.title} ${value}`}
                              style={[
                                styles.totalCell,
                                { width: columns.total, color: palette.textPrimary },
                              ]}
                            >
                              {value}
                            </Text>
                          );
                        })}
                      </View>
                    ))}
                  </View>
                </ScrollView>
              </View>
              <View style={styles.pager}>
                <Text style={[styles.pagerLabel, { color: palette.textSecondary }]}>
                  {`Showing ${rangeStart}–${rangeEnd} of ${totalRecords}`}
                </Text>
                <View style={styles.pagerActions}>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Previous page"
                    disabled={page <= 1 || loading}
                    onPress={() => void fetchReport(page - 1)}
                  >
                    <Text
                      style={[
                        styles.pagerButton,
                        { color: page <= 1 ? palette.textMuted : palette.primary },
                      ]}
                    >
                      Previous
                    </Text>
                  </Pressable>
                  <Text style={[styles.pagerLabel, { color: palette.textPrimary }]}>
                    {`Page ${page} of ${totalPages}`}
                  </Text>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Next page"
                    disabled={page >= totalPages || loading}
                    onPress={() => void fetchReport(page + 1)}
                  >
                    <Text
                      style={[
                        styles.pagerButton,
                        {
                          color:
                            page >= totalPages ? palette.textMuted : palette.primary,
                        },
                      ]}
                    >
                      Next
                    </Text>
                  </Pressable>
                </View>
              </View>
            </ScrollView>
          )}
        </View>
      </AppScreen>
  );
}

export function EmployeeMonthlyAttendanceReportScreen(props: Props) {
  return (
    <ReportAccessGuard reportCode={REPORT_ACCESS_CODES.EMPLOYEE_MONTHLY_ATTENDANCE}>
      <EmployeeMonthlyAttendanceReportBody {...props} />
    </ReportAccessGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  body: { flex: 1, paddingBottom: spacing.lg },
  monthBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  monthLabel: { ...typography.body, fontWeight: '700', flex: 1, textAlign: 'center' },
  chevron: { fontSize: 28, fontWeight: '600', paddingHorizontal: spacing.sm },
  selectorRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  yearStepper: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  yearChevron: { fontSize: 22, fontWeight: '600', paddingHorizontal: spacing.xs },
  yearLabel: { ...typography.body, fontWeight: '700' },
  monthChips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, flexShrink: 1 },
  monthChip: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  monthChipText: { ...typography.caption, fontWeight: '700' },
  thisMonth: {
    ...typography.caption,
    fontWeight: '700',
    marginTop: spacing.sm,
    marginBottom: spacing.md,
  },
  search: {
    ...typography.body,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  downloadRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  downloadButton: { flex: 1 },
  message: {
    ...typography.caption,
    fontWeight: '600',
    marginBottom: spacing.sm,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: spacing.lg,
    rowGap: spacing.xs,
    marginBottom: spacing.md,
  },
  legendItem: { flexDirection: 'row', gap: spacing.xs },
  legendCode: { ...typography.caption, fontWeight: '700' },
  legendLabel: { ...typography.caption },
  tableScroll: { flex: 1 },
  table: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  frozenColumn: { borderRightWidth: StyleSheet.hairlineWidth },
  frozenCell: { paddingLeft: spacing.sm, paddingRight: spacing.xs },
  datesScroll: { flex: 1 },
  scrollRow: { paddingLeft: spacing.xs, paddingRight: spacing.sm, gap: spacing.xs },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    height: MONTHLY_HEADER_HEIGHT,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: spacing.sm,
  },
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: MONTHLY_ROW_HEIGHT,
  },
  headerCell: {
    ...typography.caption,
    fontWeight: '700',
  },
  dateCell: {
    ...typography.caption,
    marginTop: 2,
  },
  center: { textAlign: 'center' },
  dayHeader: { gap: 0 },
  employeeName: {
    ...typography.body,
    fontWeight: '700',
    fontSize: 14,
  },
  statusCell: {
    ...typography.caption,
    fontWeight: '700',
  },
  totalCell: {
    ...typography.body,
    fontWeight: '700',
    fontSize: 14,
  },
  pager: {
    marginTop: spacing.md,
    gap: spacing.sm,
  },
  pagerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  pagerLabel: { ...typography.caption, fontWeight: '600' },
  pagerButton: { ...typography.body, fontWeight: '700' },
});
